import { type EditorSelection, type EditorState, type Extension, StateEffect, StateField } from "@codemirror/state";
import {
    Decoration,
    type DecorationSet,
    EditorView,
    type Panel,
    type ViewUpdate,
    runScopeHandlers,
} from "@codemirror/view";
import {
    SearchQuery,
    closeSearchPanel,
    findNext,
    findPrevious,
    getSearchQuery,
    openSearchPanel,
    replaceAll,
    replaceNext,
    search,
    searchPanelOpen,
    setSearchQuery,
} from "@codemirror/search";

import { keyLabel, matches, type KeyInfo } from "./commands.ts";

/** Counting stops here, and the count reads "N+". */
const MATCH_LIMIT = 10000;

type Toggle = "case" | "word" | "regex" | "selection";

/** The widget's own keys, for its handler, its tooltips and the keyboard shortcuts list. */
const KEYS = {
    case: { title: "Match Case", key: "Alt-c" },
    word: { title: "Match Whole Word", key: "Alt-w" },
    regex: { title: "Use Regular Expression", key: "Alt-r" },
    selection: { title: "Find in Selection", key: "Alt-l" },
    previous: { title: "Previous Match", key: "Shift-Enter" },
    next: { title: "Next Match", key: "Enter" },
    replace: { title: "Replace", key: "Enter" },
    replaceAll: { title: "Replace All", key: "Mod-Enter" },
    close: { title: "Close", key: "Escape" },
};

/** A button's tooltip: its title and key. */
function described(name: keyof typeof KEYS): string {
    return `${KEYS[name].title} (${keyLabel(KEYS[name].key)})`;
}

/** The find widget's keys, which work while the focus is in it. */
export function findKeys(): KeyInfo[] {
    return Object.values(KEYS).map(({ title, key }) => ({ title, category: "Find", keys: [key] }));
}

/** The ranges that "Find in Selection" limits the search to. They are dropped when the widget closes. */
const setScope = StateEffect.define<{ from: number; to: number }[] | null>();
const scopeMark = Decoration.mark({ class: "wb-find-scope" });
const scopeField = StateField.define<DecorationSet>({
    create: () => Decoration.none,
    update(scope, tr) {
        if (!searchPanelOpen(tr.state)) return Decoration.none;
        scope = scope.map(tr.changes);
        for (const effect of tr.effects) {
            if (!effect.is(setScope)) continue;
            scope =
                effect.value === null
                    ? Decoration.none
                    : Decoration.set(
                          effect.value.filter((r) => r.from < r.to).map((r) => scopeMark.range(r.from, r.to)),
                      );
        }
        return scope;
    },
    provide: (field) => EditorView.decorations.from(field),
});

function inScope(_match: string, state: EditorState, from: number, to: number): boolean {
    const scope = state.field(scopeField, false);
    if (scope === undefined || scope.size === 0) return true;
    let inside = false;
    scope.between(from, to, (start, end) => {
        if (start <= from && end >= to) inside = true;
        return inside ? false : undefined;
    });
    return inside;
}

const widgets = new WeakMap<EditorView, FindWidget>();
let replaceShown = false;

/**
 * VS Code's find widget in the editor's top right corner: a chevron that shows
 * the replace row, the find field with its three toggles, the match count, and
 * buttons for previous, next, find in selection and close. It edits the query
 * of CodeMirror's search state, so highlighting and F3 keep working.
 */
class FindWidget implements Panel {
    readonly dom: HTMLElement;
    readonly top = true;
    readonly #view: EditorView;
    readonly #expand: HTMLButtonElement;
    readonly #find: HTMLInputElement;
    readonly #replace: HTMLInputElement;
    readonly #count: HTMLElement;
    readonly #toggles: Record<Toggle, HTMLButtonElement>;
    readonly #actions: HTMLButtonElement[];
    /** Match ranges as from, to pairs, recounted when the text or the query changes. */
    #matches: number[] = [];
    #capped = false;
    /** The selection when the widget opened, which typing in the find field replaces with a match. */
    readonly #opened: EditorSelection;

    constructor(view: EditorView) {
        this.#view = view;
        this.#opened = view.state.selection;
        widgets.set(view, this);
        this.dom = document.createElement("div");
        this.dom.className = "wb-find";
        this.dom.addEventListener("keydown", (event) => this.#key(event));

        this.#expand = this.#button("chevron-right", "Toggle Replace", () => this.expand(!replaceShown));
        this.#expand.classList.add("wb-find-expand");
        this.#find = this.#input("Find", true);
        this.#replace = this.#input("Replace", false);
        this.#toggles = {
            case: this.#toggle("case-sensitive", described("case")),
            word: this.#toggle("whole-word", described("word")),
            regex: this.#toggle("regex", described("regex")),
            selection: this.#toggle("list-selection", described("selection")),
        };
        this.#count = document.createElement("span");
        this.#count.className = "wb-find-count";
        this.#actions = [
            this.#button("arrow-up", described("previous"), () => findPrevious(view)),
            this.#button("arrow-down", described("next"), () => findNext(view)),
            this.#button("replace", described("replace"), () => replaceNext(view)),
            this.#button("replace-all", described("replaceAll"), () => replaceAll(view)),
        ];
        const [previous, next, replace, all] = this.#actions as [
            HTMLButtonElement,
            HTMLButtonElement,
            HTMLButtonElement,
            HTMLButtonElement,
        ];

        const findField = document.createElement("div");
        findField.className = "wb-find-field";
        findField.append(this.#find, this.#toggles.case, this.#toggles.word, this.#toggles.regex);
        const findButtons = document.createElement("div");
        findButtons.className = "wb-find-buttons";
        findButtons.append(
            previous,
            next,
            this.#toggles.selection,
            this.#button("close", described("close"), () => closeSearchPanel(view)),
        );
        const replaceField = document.createElement("div");
        replaceField.className = "wb-find-field";
        replaceField.append(this.#replace);
        const replaceButtons = document.createElement("div");
        replaceButtons.className = "wb-find-buttons wb-find-replace";
        replaceButtons.append(replace, all);
        replaceField.classList.add("wb-find-replace");
        const rows = document.createElement("div");
        rows.className = "wb-find-rows";
        rows.append(findField, this.#count, findButtons, replaceField, replaceButtons);
        this.dom.append(this.#expand, rows);

        this.#show(getSearchQuery(view.state));
        this.#setReplaceShown(replaceShown);
        this.#recount();
        this.#label();
    }

    /** Show or hide the replace row, and focus the field the user types in next. */
    expand(show: boolean): void {
        this.#setReplaceShown(show);
        const field = show && this.#find.value !== "" ? this.#replace : this.#find;
        field.focus();
        field.select();
    }

    mount(): void {
        this.#find.select();
    }

    update(update: ViewUpdate): void {
        let stale = update.docChanged;
        for (const tr of update.transactions) {
            for (const effect of tr.effects) {
                if (effect.is(setSearchQuery)) {
                    if (!effect.value.eq(this.#query())) this.#show(effect.value);
                    stale = true;
                }
                if (effect.is(setScope)) stale = true;
            }
        }
        if (stale) this.#recount();
        if (stale || update.selectionSet) this.#label();
    }

    destroy(): void {
        widgets.delete(this.#view);
    }

    #setReplaceShown(show: boolean): void {
        replaceShown = show;
        this.dom.classList.toggle("replace-shown", show);
        this.#expand.className = `wb-find-expand codicon codicon-chevron-${show ? "down" : "right"}`;
        this.#expand.setAttribute("aria-expanded", String(show));
    }

    #input(placeholder: string, main: boolean): HTMLInputElement {
        const input = document.createElement("input");
        input.type = "text";
        input.placeholder = placeholder;
        input.spellcheck = false;
        input.setAttribute("aria-label", placeholder);
        if (main) input.setAttribute("main-field", "true");
        input.addEventListener("input", () => this.#commit(main));
        return input;
    }

    #button(icon: string, title: string, run: () => void): HTMLButtonElement {
        const button = document.createElement("button");
        button.type = "button";
        button.className = `codicon codicon-${icon}`;
        button.title = title;
        button.setAttribute("aria-label", title);
        // Keep the focus in the field, as VS Code does.
        button.addEventListener("mousedown", (event) => event.preventDefault());
        button.addEventListener("click", run);
        return button;
    }

    #toggle(icon: string, title: string): HTMLButtonElement {
        const button = this.#button(icon, title, () => this.#flip(button));
        button.classList.add("wb-find-toggle");
        button.setAttribute("aria-pressed", "false");
        return button;
    }

    #flip(button: HTMLButtonElement): void {
        const on = button.getAttribute("aria-pressed") !== "true";
        button.setAttribute("aria-pressed", String(on));
        if (button !== this.#toggles.selection) return this.#commit(false);
        let scope = null;
        if (on) {
            const { state } = this.#view;
            const { from, to } = state.selection.main;
            const onMatch = this.#matches.some((at, i) => i % 2 === 0 && at === from && this.#matches[i + 1] === to);
            const selection =
                onMatch && this.#opened.ranges.some((range) => !range.empty) ? this.#opened : state.selection;
            scope = selection.ranges.map((range) => {
                if (!range.empty) return { from: range.from, to: range.to };
                const line = state.doc.lineAt(range.head);
                return { from: line.from, to: line.to };
            });
        }
        this.#view.dispatch({ effects: [setScope.of(scope), setSearchQuery.of(this.#query())] });
    }

    #on(name: Toggle): boolean {
        return this.#toggles[name].getAttribute("aria-pressed") === "true";
    }

    #query(): SearchQuery {
        return new SearchQuery({
            search: this.#find.value,
            replace: this.#replace.value,
            caseSensitive: this.#on("case"),
            wholeWord: this.#on("word"),
            regexp: this.#on("regex"),
            literal: true,
            test: this.#on("selection") ? inScope : undefined,
        });
    }

    #show(query: SearchQuery): void {
        this.#find.value = query.search;
        this.#replace.value = query.replace;
        this.#toggles.case.setAttribute("aria-pressed", String(query.caseSensitive));
        this.#toggles.word.setAttribute("aria-pressed", String(query.wholeWord));
        this.#toggles.regex.setAttribute("aria-pressed", String(query.regexp));
        const scoped = (this.#view.state.field(scopeField, false)?.size ?? 0) > 0;
        this.#toggles.selection.setAttribute("aria-pressed", String(scoped));
    }

    /** Store the query, and on typing in the find field select the first match from the cursor, as VS Code does. */
    #commit(typed: boolean): void {
        const query = this.#query();
        if (!query.eq(getSearchQuery(this.#view.state))) this.#view.dispatch({ effects: setSearchQuery.of(query) });
        if (!typed || this.#matches.length === 0) return;
        const start = this.#view.state.selection.main.from;
        let at = 0;
        while (at < this.#matches.length && this.#matches[at]! < start) at += 2;
        if (at >= this.#matches.length) at = 0;
        const from = this.#matches[at]!;
        const to = this.#matches[at + 1]!;
        this.#view.dispatch({
            selection: { anchor: from, head: to },
            effects: EditorView.scrollIntoView(from, { y: "center" }),
            userEvent: "select.search",
        });
    }

    #recount(): void {
        this.#matches = [];
        this.#capped = false;
        const query = getSearchQuery(this.#view.state);
        if (!query.valid) return;
        const cursor = query.getCursor(this.#view.state);
        for (let next = cursor.next(); !next.done; next = cursor.next()) {
            if (this.#matches.length === MATCH_LIMIT * 2) {
                this.#capped = true;
                break;
            }
            this.#matches.push(next.value.from, next.value.to);
        }
    }

    #label(): void {
        const query = getSearchQuery(this.#view.state);
        const total = this.#matches.length / 2;
        const { from, to } = this.#view.state.selection.main;
        let current = "?";
        for (let i = 0; i < this.#matches.length; i += 2) {
            if (this.#matches[i] === from && this.#matches[i + 1] === to) current = String(i / 2 + 1);
            if (this.#matches[i]! >= from) break;
        }
        this.#count.textContent = total === 0 ? "No results" : `${current} of ${total}${this.#capped ? "+" : ""}`;
        this.dom.classList.toggle("no-results", total === 0 && query.search !== "");
        this.#find.classList.toggle("invalid", query.search !== "" && !query.valid);
        for (const button of this.#actions) button.disabled = total === 0;
    }

    #key(event: KeyboardEvent): void {
        const view = this.#view;
        if (runScopeHandlers(view, event, "search-panel")) {
            event.preventDefault();
            return;
        }
        const toggle = (["case", "word", "regex", "selection"] as const).find((name) => matches(KEYS[name].key, event));
        const inFind = event.target === this.#find;
        const inReplace = event.target === this.#replace;
        if (toggle !== undefined) {
            this.#flip(this.#toggles[toggle]);
        } else if (inFind && matches(KEYS.next.key, event)) {
            findNext(view);
        } else if (inFind && matches(KEYS.previous.key, event)) {
            findPrevious(view);
        } else if (inReplace && matches(KEYS.replace.key, event)) {
            replaceNext(view);
        } else if (inReplace && matches(KEYS.replaceAll.key, event)) {
            replaceAll(view);
        } else {
            return;
        }
        event.preventDefault();
    }
}

/** CodeMirror's search state with the find widget as its panel. */
export function findWidget(): Extension {
    return [search({ top: true, literal: true, createPanel: (view) => new FindWidget(view) }), scopeField];
}

/** Open the find widget with the replace row shown, as Ctrl+H does in VS Code. */
export function openReplace(view: EditorView): boolean {
    openSearchPanel(view);
    widgets.get(view)?.expand(true);
    return true;
}
